import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import SelectedApplicants from '../SelectedApplicants';
import { IExtendedActionModalProps } from '../../types';
import { classes } from '../styles';
import { useQuery } from '@tanstack/react-query';
import Api from '../../../Application/API';
import { Autocomplete, FormControl, FormLabel, TextField } from '@mui/material';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';

export default function BulkExportCSV({
  jobId,
  setSnackbar,
  modalsOpen,
  setModalsOpen,
  globalSelected,
  handleDeselectCandidate,
  filters
}: IExtendedActionModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<{
    value: number;
    label: string;
    meta: Array<{
      options?: Array<{ value: string; label: string }>;
      type?: string;
      label: string;
      data_pill_label?: string;
      column: string;
      mapping_from?: string | null;
      required?: boolean;
    }>;
  } | null>(null);
  const [customData, setCustomData] = useState<{ value: string; column: string }[]>([]);
  const [csvRows, setCsvRows] = useState<string[]>([]);
  const [templateRequiredError, setTemplateRequiredError] = useState<string>('');
  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['csv export templates'],
    queryFn: async () => {
      const { res } = await Api.getCsvExportTemplates();
      const templates = res as {
        csv_export_templates: Array<{
          value: number;
          label: string;
          meta: Array<{
            options?: Array<{ value: string; label: string }>;
            type?: string;
            label: string;
            data_pill_label?: string;
            column: string;
            mapping_from?: string | null;
            required?: boolean;
          }>;
        }>;
      };
      return templates.csv_export_templates;
    },
    onError: (e: { error: string }) =>
      setSnackbar({
        message: `There was an error getting export templates: ${e.error}`,
        state: 'error'
      }),
    initialData: [],
    enabled: globalSelected.length > 0
  });

  const {
    isFetching: loadingCandidateData,
    refetch: fetchCandidateData,
    isSuccess: candidateDataSuccess
  } = useQuery({
    queryKey: ['csv export candidate data', globalSelected.map((app) => app.id)],
    queryFn: async () => {
      if (globalSelected.length === 0 || !selectedTemplate) return;
      const { res } = await Api.getCandidateDataWithCsvTemplate(
        jobId,
        globalSelected.map((app) => app.id),
        selectedTemplate.value
      );
      const result = res as {
        csv: { rows: string[][] };
        meta: Array<{ label: string; column: string }>;
      };

      const headers = result.meta.map((row: { column: string }) => `"${row.column}"`).join(',');
      const mappedCustomData: Record<string, string> = customData.reduce((acc: Record<string, string>, item) => {
          acc[item.column] = item.value;
          return acc;
        },
        {}
      );

      const rows = result.csv.rows.map((row: string[]) =>
        row
          .map((cell: string | null, index: number) => {
            const column = result.meta[index].column;
            const customDataValue = mappedCustomData[column];
            return cell ? `"${cell}"` : customDataValue ? `"${customDataValue}"` : '';
          })
          .join(',')
      );

      setCsvRows([headers, ...rows]);
      return result;
    },
    onError: (e: { error: string }) =>
      setSnackbar({
        message: `There was an error getting candidate data: ${e.error}`,
        state: 'error'
      }),
    initialData: [],
    enabled: false
  });

  useEffect(() => {
    if (candidateDataSuccess && csvRows.length > 0) {
      const csvString = csvRows.join('\n');

      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'candidate_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setModalsOpen((prev) => ({ ...prev, exportToCsv: false }));

      setSnackbar({
        message: 'Your CSV file has been downloaded',
        state: 'success'
      });
    }
  }, [csvRows, candidateDataSuccess, setModalsOpen, setSnackbar]);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit: SubmitHandler<FieldValues> = async () => {
    fetchCandidateData();
  };

  const updateCustomData = (column: string, value: string) => {
    setCustomData((prevData) => {
      const index = prevData.findIndex((item) => item.column === column);
      if (index === -1) {
        return [...prevData, { column: column, value: value }];
      }
      return prevData.map((item) => (item.column === column ? { ...item, value: value } : item));
    });
  };

  const CandidateDataDisplay = useMemo(() => {
    if (!selectedTemplate) return null;

    return selectedTemplate.meta.map((row, index) => {
      if (row.type === 'single_select') {
        return (
          <FormControl sx={{ marginTop: '35px', width: '48%' }} key={index}>
            <Autocomplete
              sx={sharedClasses.formAutocomplete}
              {...register(row.column, {
                required: row.required ? `${row.label} is required` : false
              })}
              options={row.options ?? []}
              getOptionLabel={(option: { label: string }) => option.label}
              onChange={(_event, value) => updateCustomData(row.column, value?.value ?? '')}
              renderInput={(params: object) => (
                <TextField
                  {...params}
                  label={row.label}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{
                    ...sharedClasses.autoCompleteTextfield,
                    '& .MuiOutlinedInput-input': { border: 0 }
                  }}
                  required={row.required}
                  helperText={errors[row.column]?.message}
                />
              )}
            />
          </FormControl>
        );
      }
      return (
        <FormControl sx={{ marginTop: '16px', width: '48%' }} key={index}>
          <FormLabel required={row.required} sx={sharedClasses.formLabel}>
            {row.label}
          </FormLabel>
          <TextField
            {...register(row.column, {
              required: row.required ? `${row.label} is required` : false,
            })}
            sx={{
              '& .MuiOutlinedInput-input': {
                padding: '8px',
                border: '1px solid #DDDDDD',
                borderRadius: '6px'
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
            }}
            type={row.type === 'date' ? 'date' : 'text'}
            required={row.required}
            disabled={!!row.mapping_from}
            defaultValue={row.mapping_from && row.data_pill_label ? row.data_pill_label : null}
            onChange={(e: { target: { value: string } }) =>
              updateCustomData(row.column, e.target.value)
            }
            error={!!errors[row.column]}
            helperText={errors[row.column]?.message}
          />
        </FormControl>
      );
    });
  }, [register, selectedTemplate, errors]);

  return (
    <Modal
      open={modalsOpen.exportToCsv}
      onClose={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
      aria-labelledby="export-csv-modal"
    >
      <Box sx={classes.actionsModalReducedPadding}>
        <CloseIcon
          onClick={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
          sx={classes.closeIcon}
        />
        <Box id="export-csv-title" sx={classes.actionModalHeader}>
          Smart CSV Export
        </Box>
        <Box sx={{ marginLeft: '10px', paddingTop: 6, width: '48%' }}>
          <Autocomplete
            disablePortal
            options={templates}
            getOptionLabel={(option: { label: string }) => option.label}
            value={selectedTemplate}
            sx={{ ...sharedClasses.formAutocomplete }}
            loading={loadingTemplates}
            loadingText="Loading templates..."
            renderInput={(params: object) => (
              <TextField
                {...params}
                label="Export to"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={sharedClasses.autoCompleteTextfield}
                required
                helperText={templateRequiredError}
              />
            )}
            onChange={(_event, value) => {
              value
                ? setTemplateRequiredError('')
                : setTemplateRequiredError('Must select a template');
              setSelectedTemplate(value);
            }}
          />
        </Box>
        <Box
          sx={{ ...classes.modalContent, margin: '0 0 10px 10px' }}
          id="export-csv-modal-content"
          aria-label="Export CSV Modal Content"
        >
          {selectedTemplate && <h3 style={{ marginBottom: '10px' }}>Fields</h3>}
          <form onSubmit={handleSubmit(onSubmit)} id="BulkExportCSVForm">
            <Box display="flex" flexWrap="wrap" gap={2}>
              {CandidateDataDisplay}
            </Box>
          </form>
          <Box>
            <h3>Candidates</h3>
            <SelectedApplicants
              globalSelected={globalSelected}
              handleDeselectCandidate={handleDeselectCandidate}
              closeModalCallback={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
              filters={filters}
            />
          </Box>
        </Box>
        <Box sx={classes.modalFooter}>
          <Button
            id="close-export-csv-button"
            sx={classes.modalCancelButton}
            onClick={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
          >
            Cancel
          </Button>
          <Button
            id="submit-export-csv-button"
            type="submit"
            form="BulkExportCSVForm"
            disabled={!selectedTemplate || loadingCandidateData}
            sx={{ ...classes.modalSendButton, width: '80px', height: '41px' }}
          >
            {loadingTemplates || loadingCandidateData ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Export'
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
